import React, { useState, memo } from "react";
import { User, Mail, Phone, Pencil, KeyRound, AlertTriangle, Clock, Key, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DriverLicenseLightbox } from "./ImageCropTools";

// Memoized User Card component - styled like VehicleRowCard
export const UserCard = memo(({ user, currentUser, roleLabels, roleBadgeColors, onEdit, onResetPin, onToggleActive, onDelete }) => {
  const [showLicenseLightbox, setShowLicenseLightbox] = useState(false);
  const cropPosition = user.photo_crop || { x: 25, y: 30, zoom: 2 };
  
  const isIncomplete = user.profile_incomplete || !user.email || !user.phone || !user.photo;
  const missingFields = user.missing_fields || [];
  
  // Format timestamp helper
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };
  
  return (
    <div className="px-1 py-1.5 sm:px-2 sm:py-1">
      <div 
        className={`p-3 sm:p-4 rounded-xl border cursor-pointer transition-all shadow-sm ${
          !user.is_active 
            ? 'bg-red-50 border-red-200 hover:bg-red-100' 
            : isIncomplete 
              ? 'bg-amber-50 border-amber-200 hover:border-amber-300 hover:shadow-md' 
              : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md'
        }`}
        data-testid={`user-card-${user.id}`}
      >
        {/* Mobile Layout */}
        <div className="sm:hidden">
          {/* Row 1: Photo + Name + Role Badge */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {user.photo ? (
                <>
                  <div 
                    className="w-10 h-10 rounded-full flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                    onClick={() => setShowLicenseLightbox(true)}
                    title="Click to view driver license"
                    style={{
                      backgroundImage: `url(${user.photo})`,
                      backgroundSize: `${(cropPosition.zoom || 2) * 100}%`,
                      backgroundPosition: `${cropPosition.x}% ${cropPosition.y}%`,
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                  <DriverLicenseLightbox 
                    photo={user.photo}
                    isOpen={showLicenseLightbox}
                    onClose={() => setShowLicenseLightbox(false)}
                  />
                </>
              ) : (
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-slate-400" />
                </div>
              )}
              <div>
                <span className="font-bold text-base text-slate-900">{user.first_name} {user.last_name?.toUpperCase()}</span>
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${roleBadgeColors[user.role] || "bg-slate-100 text-slate-700"}`}>
                  {roleLabels[user.role] || user.role}
                </span>
              </div>
            </div>
          </div>
          
          {/* Row 2: Position */}
          <div className="text-sm text-slate-600 mb-2">
            {user.position}
          </div>
          
          {/* Inactive Banner */}
          {!user.is_active && (
            <div className="flex items-center gap-1.5 mb-2 py-1.5 px-2 rounded-lg bg-red-500 text-white">
              <UserX className="w-4 h-4" />
              <span className="text-xs font-semibold">INACTIVE</span>
            </div>
          )}
          
          {/* Incomplete Profile Banner */}
          {isIncomplete && user.is_active && (
            <div className="flex items-center gap-1.5 mb-2 py-1.5 px-2 rounded-lg bg-amber-500 text-white">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-semibold">NEEDS ATTENTION</span>
              {missingFields.length > 0 && <span className="text-xs opacity-80">• {missingFields.join(', ')}</span>}
            </div>
          )}
          
          {/* Row 3: Stats & Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-3">
              {/* Contact Info Pills */}
              {user.email && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-600">
                  <Mail className="w-3 h-3" />
                </span>
              )}
              {user.phone && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-600">
                  <Phone className="w-3 h-3" />
                </span>
              )}
              {/* Activity timestamps */}
              {user.last_login && (
                <span className="flex items-center gap-1 text-[10px] text-slate-400" title={`Last login: ${new Date(user.last_login).toLocaleString()}`}>
                  <Clock className="w-3 h-3" />
                  {formatTimestamp(user.last_login)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {onEdit && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onEdit(user)}
                  className="h-8 w-8 p-0"
                  title="Edit user"
                  data-testid={`btn-edit-user-${user.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {onResetPin && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onResetPin(user)}
                  className="h-8 w-8 p-0"
                  title="Reset PIN"
                  data-testid={`btn-reset-pin-${user.id}`}
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center gap-4">
          {/* User Photo */}
          {user.photo ? (
            <>
              <div 
                className="w-12 h-12 rounded-xl flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                onClick={() => setShowLicenseLightbox(true)}
                title="Click to view driver license"
                style={{
                  backgroundImage: `url(${user.photo})`,
                  backgroundSize: `${(cropPosition.zoom || 2) * 100}%`,
                  backgroundPosition: `${cropPosition.x}% ${cropPosition.y}%`,
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <DriverLicenseLightbox 
                photo={user.photo}
                isOpen={showLicenseLightbox}
                onClose={() => setShowLicenseLightbox(false)}
              />
            </>
          ) : (
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              !user.is_active ? 'bg-red-100' : 'bg-blue-50'
            }`}>
              <User className={`w-6 h-6 ${!user.is_active ? 'text-red-500' : 'text-blue-600'}`} />
            </div>
          )}

          {/* Main Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-lg">{user.last_name?.toUpperCase()}, {user.first_name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadgeColors[user.role] || "bg-slate-100 text-slate-700"}`}>
                {roleLabels[user.role] || user.role}
              </span>
              {!user.is_active && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-500 text-white font-medium">
                  <UserX className="w-3 h-3" />
                  INACTIVE
                </span>
              )}
              {isIncomplete && user.is_active && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-500 text-white font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  NEEDS ATTENTION
                </span>
              )}
            </div>
            
            <div className="text-sm text-slate-500 mt-1">
              {user.position}
            </div>
            
            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
              {user.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{user.email}</span>
                </span>
              )}
              {user.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3 flex-shrink-0" />
                  {user.phone}
                </span>
              )}
            </div>
            
            {/* Activity timestamps */}
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400 mt-2">
              {user.last_login && (
                <span className="flex items-center gap-1" title={`Last login: ${new Date(user.last_login).toLocaleString()}`}>
                  <Clock className="w-3 h-3" />
                  Login: {formatTimestamp(user.last_login)}
                </span>
              )}
              {user.pin_changed_at && (
                <span className="flex items-center gap-1" title={`PIN set: ${new Date(user.pin_changed_at).toLocaleString()}`}>
                  <Key className="w-3 h-3" />
                  PIN: {formatTimestamp(user.pin_changed_at)}
                </span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {onEdit && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onEdit(user)}
                className="flex items-center gap-1"
                data-testid={`btn-edit-user-${user.id}`}
              >
                <Pencil className="w-4 h-4" />
                <span className="hidden lg:inline">Edit</span>
              </Button>
            )}
            {onResetPin && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onResetPin(user)}
                className="flex items-center gap-1"
                data-testid={`btn-reset-pin-${user.id}`}
              >
                <KeyRound className="w-4 h-4" />
                <span className="hidden lg:inline">Reset PIN</span>
              </Button>
            )}
            {onToggleActive && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onToggleActive(user.id)}
                className={user.is_active ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                data-testid={`btn-toggle-active-${user.id}`}
              >
                {user.is_active ? "Deactivate" : "Activate"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

UserCard.displayName = 'UserCard';
